import { useRef, type CSSProperties, type ReactElement } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { color, radius, space, typography, motion as motionTokens } from "@tokens/index";
import { SlidingPill } from "@primitives/index";
import { SETTINGS_NAV_ENTRIES, isSettingsEntryActive } from "./settingsNav";

/**
 * SettingsNavBar: the horizontal pill row under the top bar on `medium`.
 *
 * Same grammar as the admin bar: a `[data-scroll-row]` with snap and edge
 * fade (global stylesheet), and a `SlidingPill` behind the selected pill.
 */

const barStyle: CSSProperties = {
    position: "relative",
    zIndex: 15,
    display: "flex",
    alignItems: "center",
    borderBottom: `1px solid ${color.border.subtle}`,
    background: color.bg.surface,
    flexShrink: 0,
};

// The edge fade is 24px wide; the track's padding keeps the first pill clear.
const trackStyle: CSSProperties = {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: space[1],
    paddingBlock: space[2],
    paddingInline: space[6],
    minWidth: 0,
};

const pillStyle = (isActive: boolean): CSSProperties => ({
    position: "relative",
    zIndex: 1,
    display: "inline-flex",
    alignItems: "center",
    gap: space[2],
    minHeight: 36,
    paddingInline: space[3],
    borderRadius: radius.pill,
    textDecoration: "none",
    color: isActive ? color.accent.primary : color.fg.muted,
    fontFamily: typography.family.system,
    fontSize: typography.scale.sm,
    fontWeight: isActive ? typography.weights.semibold : typography.weights.medium,
    lineHeight: 1,
    whiteSpace: "nowrap",
    scrollSnapAlign: "start",
    transition: `color ${motionTokens.durations.fast} ${motionTokens.easings.standard}`,
    cursor: "pointer",
});

export function SettingsNavBar(): ReactElement {
    const { pathname } = useLocation();
    const trackRef = useRef<HTMLDivElement>(null);
    const activeIndex = SETTINGS_NAV_ENTRIES.findIndex((entry) =>
        isSettingsEntryActive(pathname, entry),
    );

    return (
        <nav aria-label="Settings sections" style={barStyle} data-scroll-row="">
            <div ref={trackRef} style={trackStyle}>
                <SlidingPill
                    containerRef={trackRef}
                    activeIndex={activeIndex}
                    itemSelector="[data-settings-nav-item]"
                    style={{ borderRadius: radius.pill, background: color.bg.selected }}
                />
                {SETTINGS_NAV_ENTRIES.map((entry) => {
                    const Icon = entry.icon;
                    return (
                        <NavLink
                            key={entry.id}
                            to={entry.to}
                            data-settings-nav-item=""
                            data-hit-area=""
                            style={({ isActive }) => pillStyle(isActive)}
                        >
                            <Icon size={14} strokeWidth={2.25} aria-hidden="true" />
                            {entry.label}
                        </NavLink>
                    );
                })}
            </div>
        </nav>
    );
}
